import { useMemo } from "react";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import {
  currencyFormatter,
  toDecimal,
  IPNCreditNote,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";

import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { useAccountingSystemTerm } from "../../hooks/useAccountingSystemTerm";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useCredits } from "../../hooks/useCredits";
import { commonConfig } from "../../lib/datagrid";
import { WrappedLink } from "../WrappedLink";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { DatagridCard } from "../Common/Datagrid/DatagridCard";

export function CreditsRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const creditNotesTerm = useAccountingSystemTerm(`Credit Notes`);
  const { data: creditsRes, isLoading, isFetching } = useCredits();

  const title = useMemo(() => {
    if (accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_PREPAYMENTS)) {
      return `${creditNotesTerm} | Overpayments | Prepayments`;
    }

    return creditNotesTerm;
  }, [creditNotesTerm, currentOrg]);

  const totalRemainingCredit = useMemo(() => {
    if (!creditsRes?.credits) {
      return `-`;
    }

    const byCurrency: Record<string, number> = {};

    creditsRes.credits.forEach(credit => {
      if (!byCurrency[credit.currency]) {
        byCurrency[credit.currency] = 0;
      }

      byCurrency[credit.currency] += credit.remainingCreditCents;
    } );

    if (Object.keys(byCurrency).length > 0) {
      return Object.entries(byCurrency).map(([currency, total]) => (
        `${currencyFormatter(currency)(toDecimal(total))} ${currency}`
      )).join(`, `);
    }

    return `-`;
  }, [creditsRes]);

  const rows = creditsRes?.credits || [];

  const columns: GridColDef<IPNCreditNote>[] = useMemo(() => {
    const typeMap: Record<IPNCreditNote[`type`], string> = {
      credit_note: `Credit Note`,
      overpayment: `Overpayment`,
      prepayment: `Prepayment`,
      unapplied_payment: `Unapplied Payment`,
    } as const;

    return [
      {
        field: `issueDateStr`,
        headerName: `Issue Date`,
        flex: 1,
        sortComparator: (v1, v2) => new Date(v1 as string).getTime() - new Date(v2 as string).getTime(),
      },
      {
        field: `externalStatus`,
        headerName: `Status`,
        flex: 1,
      },
      {
        field: `externalNumber`,
        headerName: `Number`,
        flex: 1,
      },
      {
        field: `contactName`,
        headerName: `Contact`,
        flex: 1,
        renderCell: ({ row }) => {
          return (
            <WrappedLink
              to={ `/contacts/${ row.contactId }` }
            >
              <Paragraph
                variant={ `link` }
                as={ `span` }
              >
                { row.contactName }
              </Paragraph>
            </WrappedLink>
          );
        },
      },
      {
        field: `remainingCreditCents`,
        headerName: `Remaining Value`,
        type: `number`,
        flex: 1,
        valueGetter: (value, row) => toDecimal(row.remainingCreditCents),
        valueFormatter: (value, row) => currencyFormatter(row.currency)(value as number),
      },
      {
        field: `totalCents`,
        headerName: `Total Value`,
        type: `number`,
        flex: 1,
        valueGetter: (value, row) => toDecimal(row.totalCents),
        valueFormatter: (value, row) => currencyFormatter(row.currency)(value as number),
      },
      {
        field: `type`,
        headerName: `Type`,
        flex: 1,
        valueFormatter: v => typeMap[v as IPNCreditNote[`type`]] || String(v),
      },
      {
        field: `currency`,
        headerName: `Currency`,
        flex: 1,
      },
    ];
  }, []);

  return (
    <div>
      <MultiButtonCardHeader
        title={ title }
        variant={ `plain` }
        description={ `Total Remaining Credit: ${totalRemainingCredit}` }
      />
      <DatagridCard className={ `h-[650px] mt-4` }>
        <DataGridPro
          rows={ rows }
          columns={ columns }
          loading={ isLoading || isFetching }
          rowHeight={ 38 }
          sx={ { ...commonConfig.headerStyle } }
        />
      </DatagridCard>
    </div>
  );
}
